import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Save } from "lucide-react";
import AnexosSection from "./detalhe/AnexosSection";

// Campos editáveis (whitelist do backend) agrupados por seção
const SECTIONS = [
  {
    title: "Dados Pessoais",
    fields: [
      ["nome_completo", "Nome Completo"],
      ["rg", "RG"], ["cnh", "CNH"],
      ["data_nascimento", "Data de Nascimento", "date"],
      ["sexo", "Sexo"], ["estado_civil", "Estado Civil"],
      ["telefone", "Telefone"], ["whatsapp", "WhatsApp"],
      ["email", "E-mail", "email"],
      ["grau_escolaridade", "Escolaridade"], ["ocupacao", "Ocupação"],
    ],
  },
  {
    title: "Cônjuge",
    fields: [
      ["conjuge_nome", "Nome do Cônjuge"],
      ["conjuge_cpf", "CPF do Cônjuge"],
      ["conjuge_rg", "RG do Cônjuge"],
      ["conjuge_data_nascimento", "Data de Nascimento", "date"],
    ],
  },
  {
    title: "Endereço",
    fields: [
      ["cep", "CEP"], ["rua", "Rua"], ["numero", "Número"],
      ["complemento", "Complemento"], ["bairro", "Bairro"],
      ["cidade", "Cidade"], ["estado", "Estado"],
      ["tipo_moradia", "Tipo de Moradia (própria/alugada)"],
      ["area_de_risco", "Área de Risco (true/false)", "boolean"],
    ],
  },
  {
    title: "Família",
    fields: [
      ["qtd_moradores", "Qtd Moradores", "number"],
      ["qtd_dependentes", "Dependentes", "number"],
      ["qtd_criancas", "Crianças", "number"],
      ["qtd_idosos", "Idosos", "number"],
      ["qtd_pcd", "PCD", "number"],
    ],
  },
  {
    title: "Eleitoral",
    fields: [
      ["titulo_eleitor", "Título de Eleitor"],
      ["zona_eleitoral", "Zona"],
      ["secao_eleitoral", "Seção"],
    ],
  },
  {
    title: "Social",
    fields: [
      ["possui_cadunico", "Possui CadÚnico", "boolean"],
      ["nis", "NIS"],
      ["beneficiario_programa", "Beneficiário Programa", "boolean"],
      ["qual_programa", "Qual Programa"],
      ["recebe_bolsa_familia", "Recebe Bolsa Família", "boolean"],
    ],
  },
  {
    title: "PNE / Idoso",
    fields: [
      ["possui_pcd", "Possui PCD", "boolean"],
      ["descricao_pcd", "Descrição PCD"],
      ["tipo_deficiencia", "Tipo de Deficiência"],
      ["cid_pne", "CID PNE"],
      ["pne_e_responsavel", "PNE é Responsável", "boolean"],
      ["possui_idoso", "Possui Idoso", "boolean"],
      ["idade_idoso", "Idade do Idoso", "number"],
      ["descricao_idoso", "Descrição Idoso"],
      ["idoso_e_responsavel", "Idoso é Responsável", "boolean"],
    ],
  },
  {
    title: "Renda",
    fields: [
      ["renda_responsavel", "Renda do Responsável (R$)", "number"],
      ["renda_conjuge", "Renda do Cônjuge (R$)", "number"],
      ["outras_rendas", "Outras Rendas (R$)", "number"],
    ],
  },
];

const parseValue = (val, type) => {
  if (val === "" || val === null || val === undefined) return null;
  if (type === "number") return Number(val);
  if (type === "boolean") return val === "true" || val === true;
  return val;
};

const INPUT_TYPE_MAP = { date: "date", number: "number", email: "email" };
const inputTypeFor = (t) => INPUT_TYPE_MAP[t] || "text";

// Converte booleano/undefined para o value do <select>
function boolSelectValue(v) {
  if (v === true) return "true";
  if (v === false) return "false";
  return "";
}

export default function EditarInscricaoPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [insc, setInsc] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/inscricoes/${id}`);
      setInsc(r.data);
      setForm(r.data);
    } catch (e) {
      logger.error("Falha ao carregar", e);
      toast.error("Falha ao carregar inscrição");
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const salvar = async () => {
    setSaving(true);
    try {
      // monta payload apenas com campos editáveis
      const payload = {};
      SECTIONS.forEach((s) => {
        s.fields.forEach(([k, , t]) => {
          const v = form[k];
          if (v !== undefined) payload[k] = parseValue(v, t);
        });
      });
      await api.put(`/inscricoes/${id}`, payload);
      toast.success("Inscrição atualizada com sucesso");
      nav(`/admin/inscricoes/${id}`);
    } catch (e) {
      logger.error("Falha ao salvar", e);
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (!insc) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="editar-inscricao-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />Voltar
          </Button>
          <h1 className="text-2xl font-black text-[#083CA6] mt-2">Editar Inscrição</h1>
          <p className="text-sm text-slate-600 mt-1">
            Protocolo <span className="font-mono">{insc.protocolo}</span> · {insc.nome_completo}
          </p>
        </div>
        <Button
          onClick={salvar}
          disabled={saving}
          className="bg-[#00883A] hover:bg-[#006e2e]"
          data-testid="btn-salvar-edicao"
        >
          <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alterações"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {SECTIONS.map((sec) => (
          <Card key={sec.title}>
            <CardHeader><CardTitle className="text-base text-[#083CA6]">{sec.title}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {sec.fields.map(([k, label, type]) => (
                <div key={k} className={type === "boolean" ? "sm:col-span-2" : ""}>
                  <Label htmlFor={k} className="text-xs text-slate-600">{label}</Label>
                  {type === "boolean" ? (
                    <select
                      id={k}
                      className="w-full border rounded px-3 py-2 text-sm mt-1"
                      value={boolSelectValue(form[k])}
                      onChange={(e) => setField(k, e.target.value === "true")}
                      data-testid={`edit-${k}`}
                    >
                      <option value="">—</option>
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </select>
                  ) : (
                    <Input
                      id={k}
                      type={inputTypeFor(type)}
                      value={form[k] ?? ""}
                      onChange={(e) => setField(k, e.target.value)}
                      data-testid={`edit-${k}`}
                    />
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>

      <AnexosSection inscricao={insc} onChanged={load} />

      <div className="flex justify-end">
        <Button
          onClick={salvar}
          disabled={saving}
          className="bg-[#00883A] hover:bg-[#006e2e]"
          data-testid="btn-salvar-edicao-bottom"
        >
          <Save className="w-4 h-4 mr-2" />{saving ? "Salvando..." : "Salvar alterações"}
        </Button>
      </div>
    </div>
  );
}
